import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type IconType = 'tabler' | 'hero-outline' | 'hero-solid';

/**
 * Icon Component - Unified icon component supporting Tabler Icons and HeroIcons
 *
 * @example
 * <!-- Tabler Icons (default) -->
 * <app-atom-icon name="folder" size="md"></app-atom-icon>
 * <app-atom-icon name="ti-folder" size="lg"></app-atom-icon>
 *
 * <!-- With custom color -->
 * <app-atom-icon name="check" size="md" color="text-green-600"></app-atom-icon>
 */
@Component({
  selector: 'app-atom-icon',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <i [class]="iconClasses()"></i>
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `]
})
export class IconComponent {
  /** Icon name - can be with or without 'ti-' prefix for Tabler icons */
  name = input.required<string>();
  
  /** Icon size */
  size = input<IconSize>('md');
  
  /** Icon type - defaults to tabler */
  type = input<IconType>('tabler');
  
  /** Custom color class (e.g., 'text-blue-600') */
  color = input<string>('');
  
  /** Additional CSS classes */
  additionalClasses = input<string>('');

  /** Size to Tailwind class mapping */
  private sizeMap: Record<IconSize, string> = {
    'xs': 'text-xs',
    'sm': 'text-sm',
    'md': 'text-base',
    'lg': 'text-lg',
    'xl': 'text-xl',
    '2xl': 'text-2xl'
  };

  /** Computed icon classes */
  iconClasses = computed(() => {
    const classes: string[] = [];
    const iconName = this.name();
    
    // Handle Tabler icons
    if (this.type() === 'tabler') {
      // Add 'ti-' prefix if not present
      if (iconName.startsWith('ti-')) {
        classes.push(iconName);
      } else {
        classes.push(`ti-${iconName}`);
      }
    }
    
    // Add size class
    classes.push(this.sizeMap[this.size()]);
    
    // Add color if specified
    if (this.color()) {
      classes.push(this.color());
    }
    
    // Add additional classes
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }
    
    return classes.join(' ');
  });
}

/**
 * Common Tabler Icons Reference:
 * 
 * Navigation:
 * - ti-home, ti-menu, ti-chevron-right, ti-chevron-left, ti-arrow-left, ti-arrow-right
 * 
 * Actions:
 * - ti-plus, ti-pencil, ti-trash, ti-eye, ti-download, ti-upload, ti-search
 * 
 * Status:
 * - ti-check, ti-x, ti-alert-circle, ti-info-circle, ti-help-circle
 * 
 * Objects:
 * - ti-folder, ti-file, ti-image, ti-calendar, ti-clock, ti-user, ti-users
 * 
 * Media Controls:
 * - ti-control-play, ti-control-pause, ti-control-stop
 * 
 * Common UI:
 * - ti-settings, ti-bell, ti-star, ti-heart, ti-bookmark, ti-share, ti-link
 */
